import { Link } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import krebraLogo from "@/assets/krebra-logo-dark.png";

const NAV_LINKS = [
  { to: "/how-it-works", label: "Si funksionon" },
  { to: "/for-creators", label: "Për creator-at" },
  { to: "/for-brands", label: "Për brandet" },
  { to: "/faq", label: "Pyetje" },
];

export const PublicNavbar = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[hsl(var(--paper)/0.82)] border-b border-ink-soft">
      <div className="page-container flex items-center justify-between h-[68px]">
        <Link to="/" className="flex items-center" aria-label="Krebra — kryefaqja">
          <img src={krebraLogo} alt="Krebra" className="h-8 w-auto" width="180" height="60" />
        </Link>
        <nav className="hidden md:flex items-center gap-1" aria-label="Navigimi kryesor">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-[14px] font-semibold text-ink-soft hover:text-ink px-3.5 py-2 rounded-full hover:bg-[hsl(var(--ink)/0.06)] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Link to="/login" className="btn-pill">Hyr</Link>
          <Link to="/signup" className="btn-pill btn-pill-ink">Fillo tani</Link>
        </div>
        <button
          className="md:hidden p-2 rounded-lg hover:bg-[hsl(var(--ink)/0.06)]"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Mbyll menunë" : "Hap menunë"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-ink-soft bg-paper-strong p-4 space-y-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="block text-[14px] font-semibold text-ink-soft hover:text-ink px-3 py-2.5 rounded-lg hover:bg-[hsl(var(--ink)/0.06)]"
              onClick={() => setOpen(false)}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex gap-2 pt-3 border-t border-ink-soft mt-3">
            <Link to="/login" className="btn-pill flex-1 justify-center" onClick={() => setOpen(false)}>Hyr</Link>
            <Link to="/signup" className="btn-pill btn-pill-ink flex-1 justify-center" onClick={() => setOpen(false)}>Fillo tani</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const PublicFooter = () => (
  <footer className="bg-ink text-[hsl(var(--paper))] mt-24">
    <div className="page-container py-16">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-acid text-ink font-black text-lg" aria-hidden>K</span>
            <span className="text-2xl font-black tracking-tight text-[hsl(var(--paper-strong))]">krebra</span>
          </div>
          <p className="text-sm text-[hsl(var(--paper)/0.7)] leading-relaxed">
            Brand × Creator për Shqipëri &amp; Kosovë. Fushata të matshme, pagesa transparente, në EUR.
          </p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--paper)/0.5)] mb-4">Platforma</p>
          <div className="space-y-2.5">
            <Link to="/how-it-works" className="block text-sm text-[hsl(var(--paper)/0.85)] hover:text-acid transition-colors">Si funksionon</Link>
            <Link to="/for-creators" className="block text-sm text-[hsl(var(--paper)/0.85)] hover:text-acid transition-colors">Për creator-at</Link>
            <Link to="/for-brands" className="block text-sm text-[hsl(var(--paper)/0.85)] hover:text-acid transition-colors">Për brandet</Link>
          </div>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--paper)/0.5)] mb-4">Mbështetje</p>
          <div className="space-y-2.5">
            <Link to="/faq" className="block text-sm text-[hsl(var(--paper)/0.85)] hover:text-acid transition-colors">Pyetje të shpeshta</Link>
            <Link to="/contact" className="block text-sm text-[hsl(var(--paper)/0.85)] hover:text-acid transition-colors">Kontakt</Link>
            <a href="mailto:support@krebra.com" className="block text-sm text-[hsl(var(--paper)/0.85)] hover:text-acid transition-colors">support@krebra.com</a>
          </div>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--paper)/0.5)] mb-4">Ligjore</p>
          <div className="space-y-2.5">
            <span className="block text-sm text-[hsl(var(--paper)/0.6)]">Kushtet e shërbimit</span>
            <span className="block text-sm text-[hsl(var(--paper)/0.6)]">Politika e privatësisë</span>
            <span className="block text-sm text-[hsl(var(--paper)/0.6)]">Cookies</span>
          </div>
        </div>
      </div>
      <div className="border-t border-[hsl(var(--paper)/0.15)] mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-3 text-[13px] text-[hsl(var(--paper)/0.6)]">
        <span>© {new Date().getFullYear()} Krebra. Të gjitha të drejtat e rezervuara.</span>
        <span>Shqipëri &amp; Kosovë · EUR</span>
      </div>
    </div>
  </footer>
);

export const PublicLayout = () => (
  <div className="min-h-screen flex flex-col bg-paper">
    <a href="#main" className="skip-link">Kalo te përmbajtja</a>
    <PublicNavbar />
    <main id="main" className="flex-1">
      <Outlet />
    </main>
    <PublicFooter />
  </div>
);
